import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/icons';
import { Footer } from '../components/Footer';
import Header from '../components/Header';

const HomeCarePageChinese: React.FC = () => {
  // Patient personas data
  const patientPersonas = [
    {
      id: 1,
      title: "慢病患者",
      name: "张阿姨",
      age: 65,
      condition: "高血压、糖尿病",
      icon: "MonitorHeart",
      challenge: "每天需要监测血压血糖，手写记录容易丢失，数据无法分析趋势",
      solution: "现在用手机拍照设备读数，AI自动记录并生成趋势图，异常时及时提醒",
      benefit: "血压血糖控制更稳定，复查时医生能看到完整记录",
      color: "blue"
    },
    {
      id: 2,
      title: "阿尔茨海默症",
      name: "李爷爷",
      age: 78,
      condition: "早期失智",
      icon: "Psychology",
      challenge: "记忆力下降，经常忘记吃药，家人不在身边时很担心",
      solution: "智能用药提醒，时间到了手机自动响铃，语音提示吃哪些药",
      benefit: "不再漏服药物，病情控制平稳，家人更安心",
      color: "indigo"
    },
    {
      id: 3,
      title: "术后康复",
      name: "陈女士",
      age: 45,
      condition: "膝关节置换术后",
      icon: "FitnessCenter",
      challenge: "不确定康复进度是否正常，担心过度运动或运动不足",
      solution: "康复计划按阶段提醒训练动作，记录康复进展，异常情况及时预警",
      benefit: "康复进度良好，比预期提前两周恢复行走",
      color: "teal"
    }
  ];

  // Core features data
  const coreFeatures = [
    {
      id: 1,
      title: "智能识别医疗文档",
      icon: "DocumentScanner",
      benefit: "拍照即可，自动读取",
      description: "告别手写记录的烦恼。AI 自动提取关键数值，建立个人健康档案。",
      features: [
        "处方、检查报告自动分类",
        "血压计、血糖仪数值识别",
        "异常指标自动高亮预警",
        "支持历史记录趋势比对"
      ],
      gradient: "from-blue-500 to-cyan-400"
    },
    {
      id: 2,
      title: "全天候健康监测",
      icon: "MonitorHeart",
      benefit: "异常指标，及时发现",
      description: "AI 帮您看懂复杂的医疗数据。生成简单易懂的健康报告，掌握身体状况。",
      features: [
        "生命体征每日趋势追踪",
        "个性化风险评分预警",
        "就医报告一键生成",
        "家庭成员授权查看"
      ],
      gradient: "from-indigo-500 to-purple-400"
    },
    {
      id: 3,
      title: "管家式用药提醒",
      icon: "Medication",
      benefit: "按时服药，不再遗忘",
      description: "智能管家守护用药安全。根据处方自动设置提醒，避免药物相互作用风险。",
      features: [
        "个性化语音播报提醒",
        "药物冲突风险实时检测",
        "药品余量不足智能预警",
        "服药依从性详细报告"
      ],
      gradient: "from-emerald-500 to-teal-400"
    }
  ];

  // Use cases with conversational UI
  const [activeUseCase, setActiveUseCase] = useState(0);
  
  const useCases = [
    {
      id: 1,
      title: "个性化锻炼计划",
      icon: "FitnessCenter",
      scenario: "想要开始锻炼，但担心运动风险",
      messages: [
        {
          type: "user",
          content: "我有糖尿病和高血压，想开始锻炼。请帮我制定一个安全的运动计划。"
        },
        {
          type: "ai",
          content: "根据您的健康状况（糖尿病、高血压），我为您推荐一个循序渐进的方案：\n\n• **有氧运动**：每天步行30分钟，心率控制在 100-120 次/分。\n• **柔韧性训练**：每周3次太极或基础瑜伽，帮助改善循环。\n• **安全警示**：避免憋气类力量训练，运动前请确保血压在安全范围。\n\n💡 建议在开始前拍摄您最新的检查报告，我将为您微调心率预警区间。"
        }
      ]
    },
    {
      id: 2,
      title: "饮食不适咨询",
      icon: "Restaurant",
      scenario: "吃完饭不舒服，想了解原因",
      messages: [
        {
          type: "user",
          content: "这两天吃完饭总是胃胀不舒服，可能是什么原因？"
        },
        {
          type: "ai",
          content: "根据您的历史记录和目前的症状，分析如下：\n\n1. **饮食关联**：您昨天记录的晚餐包含高纤维食物，若进食过快可能导致胀气。\n2. **药物因素**：您正在服用的二甲双胍偶有胃肠道副作用，建议餐中或餐后服用。\n3. **建议建议**：尝试少食多餐，若症状持续 3 天未缓解，我已为您准备好就诊简报。\n\n💡 需要为您设置一个餐后 2 小时的症状回访提醒吗？"
        }
      ]
    },
    {
      id: 3,
      title: "用药安全检测",
      icon: "LocalPharmacy",
      scenario: "想吃保健品，但不确定是否安全",
      messages: [
        {
          type: "user",
          content: "朋友推荐我吃深海鱼油，说对心脏好。我正在吃阿司匹林，能一起吃吗？"
        },
        {
          type: "ai",
          content: "⚠️ **重要安全风险提示**\n\n检查到您目前的用药：**阿司匹林**（抗凝血药）。\n\n**风险分析**：深海鱼油（Omega-3）具有天然的抗凝血效果。两者合用会**显著增加出血风险**（如皮下瘀斑、牙龈出血）。\n\n**建议**：\n• 在未咨询主治医生前，请**暂停**服用鱼油。\n• 若医生同意使用，建议定期监测凝血功能指标。\n\n您的安全是第一位的。💡 需要我帮您搜索附近的药剂师电话吗？"
        }
      ]
    }
  ];

  // How it works steps
  const steps = [
    {
      number: "01",
      title: "快速入门",
      description: "2分钟快速注册，AI 自动引导您同步健康现状，无需繁琐录入。",
      icon: "PersonAdd"
    },
    {
      number: "02",
      title: "数据收集",
      description: "只需对着报告或设备拍照，AI 自动整理，将纸面数据变为数字趋势。",
      icon: "CameraAlt"
    },
    {
      number: "03",
      title: "智慧守护",
      description: "AI 实时监测，为您设置智能提醒、预警风险，并随时解答健康疑问。",
      icon: "Insights"
    }
  ];

  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: "使用这些功能需要购买额外的硬件吗？",
      answer: "不需要。您只需要一部能拍照的智能手机即可使用我们的文档识别、用药提醒和 AI 健康问答等所有核心功能。"
    },
    {
      question: "AI 识别医疗报告的准确率有多高？",
      answer: "我们的医疗专用 OCR 模型针对中文医疗文档进行了深度优化，关键指标识别准确率超过 98%。为了安全起见，所有识别结果都会在界面显著提示您进行核对。"
    },
    {
      question: "我的健康数据隐私如何保障？",
      answer: "我们采用医疗级的 AES-256 加密技术。所有数据存储在国内合规的云服务器中，完全符合《个人信息保护法》。您可以随时导出或永久注销所有数据。"
    },
    {
      question: "它能代替医生做诊断吗？",
      answer: "不能。AgilisCare AI 定位为您的居家养护助手，负责数据整理、提醒和基础健康建议。任何医疗决策和诊断必须由专业医生做出。"
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-primary-100 selection:text-primary-900">
      <Header currentPage="chinese" />

      <main className="pt-16">
        {/* Hero Section - Dynamic Breathing Glow Design */}
        <section className="relative pt-20 pb-32 overflow-hidden">
          {/* Advanced Background System */}
          <div className="absolute inset-0 z-0 pointer-events-none">
            {/* Base Mesh Gradient */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(248,250,252,1)_0%,rgba(255,255,255,1)_100%)]" />
            
            {/* Animated Glow Blobs - "Dawn Guardian" Palette */}
            <div className="absolute top-[-10%] right-[-10%] w-[70%] h-[70%] rounded-full bg-sky-300/30 blur-[140px] animate-pulse" style={{ animationDuration: '8s' }} />
            <div className="absolute bottom-[-15%] left-[-10%] w-[60%] h-[60%] rounded-full bg-rose-200/40 blur-[120px] animate-pulse" style={{ animationDuration: '12s', animationDelay: '2s' }} />
            <div className="absolute top-[20%] left-[5%] w-[50%] h-[50%] rounded-full bg-amber-200/30 blur-[100px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '4s' }} />
            <div className="absolute bottom-[20%] right-[10%] w-[40%] h-[40%] rounded-full bg-teal-200/20 blur-[90px] animate-pulse" style={{ animationDuration: '15s', animationDelay: '1s' }} />

            {/* Subtle Texture Layer */}
            <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }} />
            
            {/* Manual Grid Implementation - Increased opacity */}
            <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(to right, rgba(15, 23, 42, 0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(15, 23, 42, 0.06) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            {/* Decorative Medical/Chemical Icons */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-[15%] left-[10%] opacity-[0.04] rotate-12">
                <Icon iconName="Science" size={120} />
              </div>
              <div className="absolute top-[20%] right-[15%] opacity-[0.03] -rotate-12">
                <Icon iconName="Biotech" size={180} />
              </div>
              <div className="absolute bottom-[25%] left-[15%] opacity-[0.03] rotate-45">
                <Icon iconName="Medication" size={100} />
              </div>
              <div className="absolute bottom-[15%] right-[10%] opacity-[0.04] -rotate-12">
                <Icon iconName="HealthAndSafety" size={140} />
              </div>
              <div className="absolute top-[50%] right-[30%] opacity-[0.02] rotate-90">
                <Icon iconName="Hub" size={200} />
              </div>
            </div>
            
            {/* Bottom Fade */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 border border-primary-100 mb-8 animate-fadeIn">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                </span>
                <span className="text-sm font-semibold text-primary-700 tracking-wide uppercase">AI 居家康复守护者</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-black tracking-tight text-slate-900 mb-8 max-w-4xl leading-[1.1]">
                让居家养护更<span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-indigo-600">从容</span>，让健康更<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-primary-600">智能</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-600 max-w-2xl mb-12 leading-relaxed">
                AgilisCare AI 帮助慢病患者与康复群体，通过智能文档识别与多维监测，构建您的个人智慧健康管家。
              </p>
              
              <div className="flex flex-col sm:flex-row items-center gap-4 animate-fadeIn">
                <Link
                  to="/register"
                  className="w-full sm:w-auto px-10 py-5 bg-slate-900 text-white text-lg font-bold rounded-2xl hover:bg-primary-600 hover:scale-105 transition-all duration-300 shadow-[0_20px_50px_rgba(0,0,0,0.1)]"
                >
                  免费开始健康管理
                </Link>
                <Link
                  to="#features"
                  className="w-full sm:w-auto px-10 py-5 bg-white text-slate-700 text-lg font-bold rounded-2xl border border-slate-200 hover:bg-slate-50 transition-all duration-300"
                >
                  探索核心功能
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="mt-20 flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all">
                <div className="flex items-center gap-2">
                  <Icon iconName="VerifiedUser" size={24} className="text-slate-900" />
                  <span className="font-bold tracking-tighter text-slate-900">医疗级加密保护</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon iconName="Security" size={24} className="text-slate-900" />
                  <span className="font-bold tracking-tighter text-slate-900">100% 隐私掌控</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon iconName="Speed" size={24} className="text-slate-900" />
                  <span className="font-bold tracking-tighter text-slate-900">秒级文档识别</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Story Section - Replacing Data Cards with more empathetic layout */}
        <section className="py-24 bg-slate-50/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
              <div className="max-w-2xl">
                <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em] mb-4">Real Life Stories</h2>
                <h3 className="text-4xl md:text-5xl font-bold text-slate-900">在每一个养护场景，<br />AI 都在默默守护。</h3>
              </div>
              <p className="text-lg text-slate-500 max-w-sm">
                我们深入了解不同群体的需求，将复杂的技术转化为最简单贴心的关怀。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {patientPersonas.map((persona) => (
                <div key={persona.id} className="group flex flex-col bg-white rounded-[2.5rem] p-10 border border-slate-100 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.08)] transition-all duration-500">
                  <div className={`w-16 h-16 rounded-2xl bg-${persona.color}-50 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform`}>
                    <Icon iconName={persona.icon} size={32} className={`text-${persona.color}-600`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl font-bold text-slate-900">{persona.name}</span>
                      <span className="px-3 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-bold uppercase tracking-wider">{persona.title}</span>
                    </div>
                    <p className="text-slate-400 font-medium mb-8">{persona.condition} · {persona.age}岁</p>
                    
                    <div className="space-y-6">
                      <div className="relative pl-6 border-l-2 border-red-100">
                        <span className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-red-50 flex items-center justify-center">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                        </span>
                        <p className="text-slate-600 text-sm leading-relaxed">{persona.challenge}</p>
                      </div>
                      <div className="relative pl-6 border-l-2 border-primary-100">
                        <span className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-primary-50 flex items-center justify-center">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                        </span>
                        <p className="text-slate-900 text-sm font-bold leading-relaxed">{persona.solution}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-10 pt-8 border-t border-slate-50 flex items-center justify-between">
                    <span className="text-sm font-bold text-emerald-600">改善：{persona.benefit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Grid - More Professional Layout */}
        <section id="features" className="py-24 bg-white relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-24">
              <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em] mb-4">Capabilities</h2>
              <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">智慧养护，触手可及</h3>
              <p className="text-xl text-slate-500 max-w-2xl mx-auto">无需专业医学背景，也能像医生一样精准管理健康。</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {coreFeatures.map((feature) => (
                <div key={feature.id} className="relative group">
                  <div className={`absolute -inset-4 rounded-[3rem] bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} />
                  <div className="relative">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} text-white shadow-lg mb-8`}>
                      <Icon iconName={feature.icon} size={32} />
                    </div>
                    <h4 className="text-2xl font-bold text-slate-900 mb-4">{feature.title}</h4>
                    <div className="inline-block px-3 py-1 bg-slate-900 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-md mb-6">
                      {feature.benefit}
                    </div>
                    <p className="text-slate-600 leading-relaxed mb-8">{feature.description}</p>
                    <ul className="space-y-4">
                      {feature.features.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Icon iconName="CheckCircle" size={20} className="text-emerald-500 mt-0.5 shrink-0" />
                          <span className="text-slate-700 font-medium">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases - Better Chat UI */}
        <section className="py-24 bg-slate-900 overflow-hidden relative">
          <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:40px_40px]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="flex flex-col lg:flex-row items-center gap-20">
              <div className="lg:w-5/12">
                <h2 className="text-sm font-black text-primary-400 uppercase tracking-[0.3em] mb-4">Interactive Demo</h2>
                <h3 className="text-4xl md:text-5xl font-bold text-white mb-8">随时随地，<br />咨询您的 AI 助手。</h3>
                <div className="flex flex-col gap-4">
                  {useCases.map((useCase, index) => (
                    <button
                      key={useCase.id}
                      onClick={() => setActiveUseCase(index)}
                      className={`flex items-center gap-4 p-5 rounded-2xl transition-all duration-300 text-left ${
                        activeUseCase === index 
                        ? 'bg-white text-slate-900 shadow-[0_20px_40px_rgba(0,0,0,0.3)] scale-105' 
                        : 'text-slate-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${activeUseCase === index ? 'bg-primary-100 text-primary-600' : 'bg-white/10 text-white'}`}>
                        <Icon iconName={useCase.icon} size={24} />
                      </div>
                      <span className="text-lg font-bold">{useCase.title}</span>
                      {activeUseCase === index && <Icon iconName="ChevronRight" size={20} className="ml-auto" />}
                    </button>
                  ))}
                </div>
              </div>

              <div className="lg:w-7/12 w-full">
                <div className="bg-slate-800 rounded-[2.5rem] border border-slate-700 shadow-2xl overflow-hidden">
                  <div className="bg-slate-700/50 p-6 flex items-center border-b border-slate-700">
                    <div className="flex gap-1.5 mr-6">
                      <div className="w-3 h-3 rounded-full bg-red-500/50" />
                      <div className="w-3 h-3 rounded-full bg-amber-500/50" />
                      <div className="w-3 h-3 rounded-full bg-emerald-500/50" />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                        <Icon iconName="SmartToy" size={18} className="text-white" />
                      </div>
                      <span className="text-white font-bold tracking-tight">AgilisCare AI Health Bot</span>
                    </div>
                  </div>
                  
                  <div className="p-8 space-y-8 min-h-[500px] flex flex-col justify-end">
                    {useCases[activeUseCase].messages.map((message, i) => (
                      <div key={i} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-slideIn`}>
                        <div className={`max-w-[85%] rounded-[2rem] p-6 ${
                          message.type === 'user' 
                          ? 'bg-primary-600 text-white rounded-br-none' 
                          : 'bg-slate-700 text-slate-100 rounded-bl-none'
                        }`}>
                          <p className="text-base leading-relaxed whitespace-pre-line">{message.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="p-6 bg-slate-900/50 border-t border-slate-700 flex gap-4">
                    <div className="flex-1 bg-slate-800 rounded-xl px-6 py-3 border border-slate-700 text-slate-500 italic flex items-center">
                      AI 正在分析您的健康问题...
                    </div>
                    <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center text-white">
                      <Icon iconName="Send" size={20} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Steps - Modern Minimalist */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="md:w-1/3">
                <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em] mb-4">Onboarding</h2>
                <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8">简单三步，<br />开启智慧养护。</h3>
                <Link to="/register" className="inline-flex items-center text-primary-600 font-bold hover:gap-4 transition-all group">
                  立即加入 <Icon iconName="ArrowForward" size={24} className="ml-2 group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="md:w-2/3 grid grid-cols-1 sm:grid-cols-3 gap-12">
                {steps.map((step) => (
                  <div key={step.number} className="group">
                    <div className="text-8xl font-black text-slate-100 mb-[-3rem] group-hover:text-primary-50 transition-colors duration-500 select-none">
                      {step.number}
                    </div>
                    <div className="relative pt-8">
                      <h4 className="text-xl font-bold text-slate-900 mb-4">{step.title}</h4>
                      <p className="text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ - Refined */}
        <section className="py-24 bg-slate-50/50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-sm font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Help Center</h2>
              <h3 className="text-4xl font-bold text-slate-900">常见问题解答</h3>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full px-8 py-6 text-left flex justify-between items-center group"
                  >
                    <span className="font-bold text-slate-800 text-lg group-hover:text-primary-600 transition-colors">
                      {faq.question}
                    </span>
                    <Icon
                      iconName="ExpandMore"
                      size={24}
                      className={`text-slate-400 transition-transform duration-300 ${expandedFaq === index ? 'rotate-180 text-primary-600' : ''}`}
                    />
                  </button>
                  {expandedFaq === index && (
                    <div className="px-8 pb-8 text-slate-600 leading-relaxed animate-fadeIn">
                      <p className="pt-2 border-t border-slate-50">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA - High Impact */}
        <section className="py-24 bg-white px-4">
          <div className="max-w-7xl mx-auto">
            <div className="relative bg-slate-900 rounded-[3rem] p-12 lg:p-24 overflow-hidden text-center">
              <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:30px_30px]" />
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary-600/20 via-transparent to-indigo-600/20" />
              
              <div className="relative z-10">
                <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 tracking-tight">
                  让科技温暖每一个家庭。
                </h2>
                <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto leading-relaxed">
                  加入数万名信任 AgilisCare AI 的用户，立即开启您的智能居家健康管理旅程。
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-6">
                  <Link
                    to="/register"
                    className="px-10 py-5 bg-white text-slate-900 text-xl font-bold rounded-2xl hover:scale-105 transition-all shadow-[0_20px_50px_rgba(255,255,255,0.1)]"
                  >
                    立即免费注册
                  </Link>
                  <Link
                    to="/cn"
                    className="px-10 py-5 bg-slate-800 text-white text-xl font-bold rounded-2xl border border-slate-700 hover:bg-slate-700 transition-all"
                  >
                    了解专业版
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomeCarePageChinese;
